import { create } from "zustand";
import { axiosInstance } from "../lib/axios";
import toast from "react-hot-toast";

export interface ResourceCost {
  _id?: string;
  label: string;
  type: "fixed" | "hourly" | "percentage";
  amount: number;
  hours?: number;
}

export interface ConnectCost {
  avgConnectsPerWin: number;
  costPerConnect: number;
}

export interface ProjectTypeOverride {
  projectType: string;
  upworkFee: number | null;
  commission: number | null;
  defaultResources?: ResourceCost[];
}

export interface FinanceSettings {
  _id?: string;
  globalUpworkFee: number;
  globalCommission: number;
  defaultCostPerConnect: number;
  defaultAvgConnectsPerWin: number;
  projectTypeOverrides: ProjectTypeOverride[];
}

export interface ProjectBreakdown {
  _id: string;
  name: string;
  projectType: string;
  projectStatus: string;

  resourceCosts: ResourceCost[];
  connectCost: ConnectCost;
  projectPrice: number | null;
  escrowAmount: number;
  releasedPayment: number;

  price: number;
  upworkFeePercent: number;
  commissionPercent: number;
  upworkFeeAmount: number;
  commissionAmount: number;
  retainedRevenue: number;
  laborCost: number;
  connectCostAmount: number;
  totalCost: number;
  netProfit: number;

  revenue: number;
  commission: number;
  tax: number;
  resourceCost: number;
}

export interface AccountSummary {
  accountId: string;
  accountName: string;
  taxPercent: number;
  commissionPercent: number;
  totalConnects: number;
  usedConnects: number;
  remainingConnects: number;
  totalBids: number;
  proposalsSent: number;
  jobsWon: number;
  winRate: number;
  revenue: number;
  upworkFee: number;
  commission: number;
  totalDeductions: number;
  laborCost: number;
  connectCost: number;
  totalCost: number;
  netProfit: number;
  avgProfitPerProject: number;
  roi: number;

  tax: number;
  resourceCost: number;
  projects: ProjectBreakdown[];
}

export interface FinanceTotals {
  revenue: number;
  netProfit: number;
  connectsUsed: number;
  jobsWon: number;
  totalBids: number;
  upworkFee: number;
  commission: number;
  laborCost: number;
  connectCost: number;
}

interface FinanceState {
  accounts: AccountSummary[];
  totals: FinanceTotals | null;
  settings: FinanceSettings | null;
  timeSeries: any[];
  isFetching: boolean;
  isSaving: boolean;

  fetchSummary: () => Promise<void>;
  fetchSettings: () => Promise<void>;
  updateSettings: (s: Partial<FinanceSettings>) => Promise<void>;
  fetchTimeSeries: (period: "daily" | "weekly" | "monthly", days?: number) => Promise<void>;
  updateAccountFinance: (id: string, taxPercent: number, commissionPercent: number) => Promise<void>;
  updateProjectCosts: (
    projectId: string,
    payload: { resourceCosts?: ResourceCost[]; connectCost?: ConnectCost; projectPrice?: number | null }
  ) => Promise<void>;
  fetchProjectSummary: (projectId: string) => Promise<any>;
}

export const useFinanceStore = create<FinanceState>((set, get) => ({
  accounts: [],
  totals: null,
  settings: null,
  timeSeries: [],
  isFetching: false,
  isSaving: false,

  fetchSummary: async () => {
    set({ isFetching: true });
    try {
      const res = await axiosInstance.get("/finance/account-summary");
      set({
        accounts: res.data.accounts,
        totals: res.data.totals,
        settings: res.data.settings || get().settings,
      });
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Failed to load finance data");
    } finally {
      set({ isFetching: false });
    }
  },

  fetchSettings: async () => {
    try {
      const res = await axiosInstance.get("/finance/settings");
      set({ settings: res.data });
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Failed to load settings");
    }
  },

  updateSettings: async (payload) => {
    set({ isSaving: true });
    try {
      const res = await axiosInstance.put("/finance/settings", payload);
      set({ settings: res.data });
      toast.success("Settings saved");
      await get().fetchSummary();
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Failed to save settings");
    } finally {
      set({ isSaving: false });
    }
  },

  fetchTimeSeries: async (period, days = 30) => {
    try {
      const res = await axiosInstance.get(`/finance/timeseries`, { params: { period, days } });
      set({ timeSeries: res.data.data });
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Failed to load timeseries");
    }
  },

  updateAccountFinance: async (id, taxPercent, commissionPercent) => {
    try {
      await axiosInstance.put(`/finance/account/${id}`, { taxPercent, commissionPercent });
      toast.success("Account updated");
      await get().fetchSummary();
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Failed to update");
    }
  },

  updateProjectCosts: async (projectId, payload) => {
    try {
      await axiosInstance.put(`/finance/project/${projectId}/resource-costs`, payload);
      toast.success("Project costs updated");
      await get().fetchSummary();
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Failed to update");
    }
  },

  fetchProjectSummary: async (projectId) => {
    try {
      const res = await axiosInstance.get(`/finance/project/${projectId}/summary`);
      return res.data;
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Failed to load summary");
      return null;
    }
  },
}));
